import React, { useMemo } from 'react';
import { UnifiedSlot } from '../InstructorDashboard';
import SlotActionBar from './SlotActionBar';

interface DashboardWeekViewProps {
  slots: UnifiedSlot[];
  currentDate: string;
  weekStart: string;
  weekEnd: string;
  onWeekChange: (offset: number) => void;
  onDayClick: (date: string) => void;
  onAttendance: (batchIds: number[], templateSlotId?: number) => void;
  onLessonPlan: (batchId: number, lessonPlanId: number, topicId?: number) => void;
  onViewTemplate: (templateId: number) => void;
  onConfirm?: (slot: UnifiedSlot) => void;
  onUnconfirm?: (slot: UnifiedSlot) => void;
}

const PIXELS_PER_30MIN = 36;
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function timeToMinutes(time: string): number {
  if (!time) return 0;
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

function formatTimeShort(time: string): string {
  if (!time) return '';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  planned: { bg: '#f5f5f5', border: '#bdbdbd' },
  'pre-scheduled': { bg: '#fff9c4', border: '#fbc02d' },
  scheduled: { bg: '#e3f2fd', border: '#1976d2' },
  conducted: { bg: '#e8f5e9', border: '#388e3c' },
  cancelled: { bg: '#ffebee', border: '#d32f2f' },
};

const DashboardWeekView: React.FC<DashboardWeekViewProps> = ({
  slots,
  currentDate,
  weekStart,
  weekEnd,
  onWeekChange,
  onDayClick,
  onAttendance,
  onLessonPlan,
  onViewTemplate,
  onConfirm,
  onUnconfirm,
}) => {
  // Build 7 day columns
  const weekDays = useMemo(() => {
    const start = parseLocalDate(weekStart);
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(toDateKey(d));
    }
    return days;
  }, [weekStart]);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = new Map<string, UnifiedSlot[]>();
    for (const slot of slots) {
      if (!slot.date) continue;
      const existing = map.get(slot.date) || [];
      existing.push(slot);
      map.set(slot.date, existing);
    }
    // Sort each day's slots by startTime
    map.forEach((daySlots) => {
      daySlots.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });
    return map;
  }, [slots]);

  // Compute time axis
  const { dayStartMinutes, dayEndMinutes, timeLabels } = useMemo(() => {
    let startMin = 7 * 60;
    let endMin = 21 * 60;

    for (const s of slots) {
      if (s.startTime) {
        const sm = timeToMinutes(s.startTime);
        if (sm < startMin) startMin = Math.floor(sm / 60) * 60;
      }
      if (s.endTime) {
        const em = timeToMinutes(s.endTime);
        if (em > endMin) endMin = Math.ceil(em / 60) * 60;
      }
    }

    const labels: { minutes: number; label: string }[] = [];
    for (let m = startMin; m <= endMin; m += 30) {
      labels.push({ minutes: m, label: formatTimeDisplay(minutesToTime(m)) });
    }

    return { dayStartMinutes: startMin, dayEndMinutes: endMin, timeLabels: labels };
  }, [slots]);

  const totalHeight = ((dayEndMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;

  const todayKey = toDateKey(new Date());

  // Format week range label
  const weekLabel = useMemo(() => {
    const s = parseLocalDate(weekStart);
    const e = parseLocalDate(weekEnd);
    const sLabel = s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const eLabel = e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${sLabel} - ${eLabel}`;
  }, [weekStart, weekEnd]);

  return (
    <div className="idash-week-view">
      {/* Week navigation */}
      <div className="idash-week-header">
        <button className="idash-nav-btn" onClick={() => onWeekChange(-1)}>&lsaquo; Prev Week</button>
        <span className="idash-week-title">{weekLabel}</span>
        <button className="idash-nav-btn" onClick={() => onWeekChange(1)}>Next Week &rsaquo;</button>
      </div>

      <div className="idash-week-grid-wrapper">
        {/* Time label header (grid row 1, col 1) */}
        <div className="idash-week-time-header" />

        {/* Day headers (grid row 1, col 2) */}
        <div className="idash-week-day-headers">
          {weekDays.map((dateKey, idx) => {
            const d = parseLocalDate(dateKey);
            const isToday = dateKey === todayKey;
            const daySlots = slotsByDate.get(dateKey) || [];
            return (
              <div
                key={dateKey}
                className={`idash-week-day-header ${isToday ? 'is-today' : ''}`}
                onClick={() => onDayClick(dateKey)}
              >
                <span className="day-name">{WEEKDAY_NAMES[idx]}</span>
                <span className="day-date">{d.getDate()}</span>
                {daySlots.length > 0 && (
                  <span className="day-count">{daySlots.length}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Time labels (grid row 2, col 1) */}
        <div className="idash-week-time-labels" style={{ height: totalHeight }}>
          {timeLabels.map(({ minutes, label }) => (
            <div
              key={minutes}
              className="idash-time-label"
              style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Time grid (grid row 2, col 2) */}
        <div className="idash-week-time-grid" style={{ height: totalHeight }}>
          {/* Grid lines */}
          {timeLabels.map(({ minutes }) => (
            <div
              key={minutes}
              className={`idash-grid-line ${minutes % 60 === 0 ? 'hour-line' : 'half-hour-line'}`}
              style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
            />
          ))}

          {/* Day column separators */}
          {weekDays.map((_, idx) => (
            <div
              key={idx}
              className="idash-week-col-sep"
              style={{ left: `${(idx / 7) * 100}%` }}
            />
          ))}

          {/* Current time indicator */}
          {weekDays.includes(todayKey) && (() => {
            const now = new Date();
            const nowMinutes = now.getHours() * 60 + now.getMinutes();
            if (nowMinutes < dayStartMinutes || nowMinutes > dayEndMinutes) return null;
            const dayIdx = weekDays.indexOf(todayKey);
            return (
              <div
                className="idash-week-current-time"
                style={{
                  top: ((nowMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN,
                  left: `${(dayIdx / 7) * 100}%`,
                  width: `${100 / 7}%`,
                }}
              />
            );
          })()}

          {/* Slot cards */}
          {weekDays.map((dateKey, dayIdx) => {
            const daySlots = slotsByDate.get(dateKey) || [];
            return daySlots.map((slot) => {
              const startMin = timeToMinutes(slot.startTime || '');
              const endMin = timeToMinutes(slot.endTime || '');
              const top = ((startMin - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
              const height = Math.max(((endMin - startMin) / 30) * PIXELS_PER_30MIN, 24);
              const colors = STATUS_COLORS[slot.status] || STATUS_COLORS.planned;
              const colWidth = 100 / 7;
              const left = dayIdx * colWidth + 0.3;

              return (
                <div
                  key={slot.templateSlotId}
                  className={`idash-week-slot-card status-${slot.status}`}
                  style={{
                    top,
                    height,
                    left: `${left}%`,
                    width: `${colWidth - 0.6}%`,
                    backgroundColor: colors.bg,
                    borderLeftColor: colors.border,
                  }}
                  title={`${formatTimeShort(slot.startTime)}-${formatTimeShort(slot.endTime)} ${slot.topicName || slot.templateName || ''}`}
                >
                  <div className="week-slot-time">
                    {formatTimeShort(slot.startTime)}-{formatTimeShort(slot.endTime)}
                  </div>
                  {height > 30 && (
                    <div className="week-slot-topic">
                      {slot.topicName || slot.templateName || 'Slot'}
                    </div>
                  )}
                  {height > 50 && slot.batchNames.length > 0 && (
                    <div className="week-slot-batch">{slot.batchNames[0]}</div>
                  )}
                  {slot.attendanceRate !== null && (
                    <div className="week-slot-attendance">{slot.attendanceRate}%</div>
                  )}
                  {height > 40 && (
                    <SlotActionBar
                      slot={slot}
                      compact={true}
                      onAttendance={onAttendance}
                      onLessonPlan={onLessonPlan}
                      onViewTemplate={onViewTemplate}
                      onConfirm={onConfirm}
                      onUnconfirm={onUnconfirm}
                    />
                  )}
                </div>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
};

export default DashboardWeekView;
